import { format } from "date-fns";
import { css, html, LitElement, PropertyValues, TemplateResult, nothing } from "lit";
import { property, customElement} from 'lit/decorators.js';
import { FiSubView, UiStateModel, UiStateModelParam } from "../../../src/models/ui-state-model";
import { VoyageShoreContextModel } from "../../../src/models/voyage-shore-context-model";
import { AlarmsByDate, groupAlarmsByDate } from "../../../src/utils/alarm-utils";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { GetFormattedDateNoYear, GetUTCTimestamp } from "../../../src/utils/helpers";
import { AlarmState, AlarmType } from "../../../src/services/types/fleet-alarms-service.types";
import { FleetAlarm, FleetAlarmLog, NotificationResponses } from "../../../src/models/types/voy-fleet-alarm-model.types";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import { VesselInfo } from "../../../src/models/types/vessels-model.types";
import { VesselVoyageVersion } from "../../voy-global-filters/src/types";
import { VesselsModel } from "../../../src/models/vessels-model";
import { ShoreContextEvent } from "../../../src/models/types/voyage-shore-context-model.types";

import "../../voyage-spinner/src/voyage-spinner";
import { DateType } from "../../voy-past-trail-view/src/types";
import { AlarmLogDropdownOptions } from "./types";
import { fromDateMethod } from "./helper";

const dropdownData = [
    AlarmLogDropdownOptions.OneMonth,
    AlarmLogDropdownOptions.ThreeMonth,
    AlarmLogDropdownOptions.SixMonth,
    AlarmLogDropdownOptions.TwelveMonth
];
import { getAlarmLogContent } from "../../acitivity-log/helper";

@customElement("voy-fleet-sub-view-vessel-alarms-log")
export class VoyFleetSubViewVesselAlarmsLog extends LitElement {
    @property({ type: Number }) public imo?: number;

    @property({ attribute: false }) private _logsByDate: AlarmsByDate = {}
    @property({ type: Object, attribute: false }) private _vessel?: VesselInfo;
    @property({ type: Array, attribute: false }) private _alarms: FleetAlarm[] =[];
    @property({ type: Array, attribute: false }) private _logList?: any[];
    @property({ type:Boolean }) private  _timeLineVisible : boolean =  false
    @property({ attribute: false }) private _initialSelectedItem: string|null = AlarmLogDropdownOptions.OneMonth;
    @property({ attribute: false }) private _fromDate: string = this._formatDateForDatepicker(fromDateMethod(new Date(),1));
    @property({ attribute: false }) private _toDate: string = this._formatDateForDatepicker(new Date());
    @property({ attribute: false }) private _datepickerMinValue: string = this._formatDateForDatepicker(new Date(new Date().getMonth() - 1));
    @property({ attribute: false }) private _selectedQuickDate: AlarmLogDropdownOptions = AlarmLogDropdownOptions.OneMonth;
    @property({ attribute: false }) private _datepickerMaxValue: string = this._formatDateForDatepicker(new Date());
    @property({ type: String }) public currentView: string = 'vessel-alarms-log';


    private _formatDateForDatepicker(date: Date) {
        return date.toISOString();
    }

    constructor() {
        super()

        this._goToSubView = this._goToSubView.bind(this);
        this._setVessel = this._setVessel.bind(this);
        this._sortAlarmLogsByNewest = this._sortAlarmLogsByNewest.bind(this);
        this._seeAlarmDetails = this._seeAlarmDetails.bind(this);
        this._getLogsForVessel = this._getLogsForVessel.bind(this);
        this._renderAlarms = this._renderAlarms.bind(this);
        this._toggleTimeLine = this._toggleTimeLine.bind(this);
        this._applyQuickDate = this._applyQuickDate.bind(this);

        if(localStorage.getItem("timeLineView")!==undefined) {
            this._timeLineVisible = localStorage.getItem("timeLineView")=="true";
        }
    }

    static styles = css`
        :host {
            font-family: MaerskText;
            font-size: 12px;
            line-height: 16px;
            font-weight: 400;
        }
        section {
            display: flex;
            flex-direction: column;
            row-gap: 16px;
        }
        h1,
        h2 {
            margin: 0
        }
        h1 {
            font-size: 26px;
            line-height: 32px;
            font-weight: 400;
            font-family: MaerskHeadline;
            color: var(--neutrals-grey-02);
        }
        section header {
            display: flex;
            flex-direction: row;
            column-gap: 4px;
            padding: 24px 16px;
        }
        header button {
            background: none;
            margin-top: 6px;
            cursor: pointer;
            padding: 0;
            border: 0;
            margin-left: -6px;
            display: flex;
            align-items: flex-start;
        }
        section:nth-of-type(1) {
            border-bottom: 1px solid var(--neutrals-grey-06);
            box-shadow: var(--voyage-box-shadow) 0px 6px 10px;
        }
        section:nth-of-type(2) {
            padding-top: 16px;
            overflow-y: auto;
        }
        .alarm-log-container {
            max-height: calc(100vh - 293px);
        }
        .alarm-log-container-full {
            max-height: calc(100vh - 235px);

        }
        .alarm-log-entry h2 {
            font-size: 16px;
            line-height: 14px;
            margin: 16px;
        }
        .alarm {
            padding: 8px 16px;
            border-bottom: 1px solid var(--neutrals-grey-06);
        }
        .alarm:first-of-type {
            border-top: 1px solid var(--neutrals-grey-06);
        }
        .alarm-info {
            font-size: 14px;
            line-height: 20px;
            margin: 0 0 2px;
        }
        .alarm-type {
            color: var(--voyage-blue-dark);
        }
        .alarm-timestamp {
            margin-left: 4px;
        }
        .alarm-state {
            color: var(--neutrals-grey-04);
            margin: 0;
        }
        #no-data {
            row-gap: 0px;
            padding: 24px 16px;
        }
        #no-data p {
            margin-bottom: 0;
        }
        .capitalize {
            text-transform: capitalize;
        }
        .loadingOverlay {
            position: absolute;
            top: -1px;
            bottom: -1px;
            right: -1px;
            left: -1px;
            z-index: 10;
            border-radius: 3px;
        }

        voyage-spinner {
            width: 50px;
            height: 50px;
            left: calc(50% - 25px);
            top: calc(50% - 25px);
            position: absolute;
        }
        .spinner-wrapper {
            position: relative;
            height: 100px;
            width: 100%;
            background-color: var(--neutrals-grey-10);
        }

        .alarm-comments-notes {
            font-family: Maersk Text;
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            color: #3C3C46;
            margin-bottom: -8px;
            margin-top: 8px;
        }

        .alarm-comments-notes p{
            margin-bottom: 8px;
            margin-top: 0px;
        }

        .alarm-comments-notes .user {
            margin-top: -6px;
            color: #878787;
        }

        .icon {
            vertical-align: middle;
        }
        .premium-icon{
            margin-right: 5px;
            margin-top: 10px;
        }

        .premium-icon mc-icon{ 
            height: 16px;
            width: 16px;
        }

        .premium-icon-h1{
            display:flex;
        }
        
        .content-container {
                padding: 0px 16px 24px 16px;
                border-bottom: 1px solid rgb(207, 207, 207);
                box-shadow: var(--voyage-box-shadow) 0px 6px 10px;
                color: var(--neutrals-grey-02);
        }
        .dates-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        }
        .dropdown-container {
            display: flex;
            justify-content: space-between;
        }
        .dropdown-title {
                display: flex;
                align-items: center;
        }
        .dropdown-wrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                position: relative;
                margin-top: 3px;
                z-index: 100;
                width: 100%;
                margin-left: 8px;
            }

            voy-dropdown {
                height: 36px;
                max-width: 100%;
                font-size: 12px;
                z-index: 100;
                margin-top: 2px;
                color: var(--neutrals-grey-02);
            }
            .input-container:focus-within {
                    /* Drop Shadow Medium */
                    box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                }

            .input-container[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }

            label {
                font-size: 14px;
            }

            .input-container input {
                display: flex;
                font-family: MaerskText;
                font-size: 12px;
                box-sizing: border-box;
                padding-left: 10px;
                padding-right: 5px;
                border: none;
                outline: none;
                color: var(--neutrals-grey-02);
                background-color: unset;
                width: 100%;
            }
            
            .input-container input::-webkit-datetime-edit-year-field {
                color: var(--neutrals-grey-02);
            }

            .inputContainer input::-webkit-calendar-picker-indicator {
                background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23bbbbbb" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
            }

            .dates-container {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
            }

            .input-and-header {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                margin-top: 16px;
            }
            .input-container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-family: MaerskText;
                box-sizing: border-box;
                height: 36px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 3px;
                margin-left: 15px;
            }
            mc-button::part(button) {
                    background-color: var(--primary-blue-900, blue);
                    color: var(--neutrals-grey-10, white);
            }
            .go-button-styles{
                margin-left:10px
            }

    `
   

private _applyQuickDate(selectedItem: string) {
    this._selectedQuickDate = selectedItem.trim() as AlarmLogDropdownOptions;
    let newFromDate = new Date();
    switch(this._selectedQuickDate) {
        case AlarmLogDropdownOptions.OneMonth:
            fromDateMethod(newFromDate,1)
            break;
        case AlarmLogDropdownOptions.ThreeMonth:
            fromDateMethod(newFromDate,3)
            break;
        case AlarmLogDropdownOptions.SixMonth:
            fromDateMethod(newFromDate,6)
            break;
        case AlarmLogDropdownOptions.TwelveMonth:
            fromDateMethod(newFromDate,12)
            break;
    }
    this._initialSelectedItem = null;
    this._fromDate = this._formatDateForDatepicker(newFromDate);
}

private _handleChangeDate (e:Event){
    if (this._fromDate > this._toDate) {
        this._fromDate = this._formatDateForDatepicker(fromDateMethod(new Date(this._toDate), 1));
        return;
    }
}
private _dateHandler(e: Event) {
    const selectedDate = (<HTMLInputElement>e.target).value;
    const inputId = (<HTMLInputElement>e.target).id;
    const parsedSelectedDate = new Date(selectedDate);

    if (inputId === DateType.fromDate) {
        this._fromDate = this._formatDateForDatepicker(parsedSelectedDate);
    } else if (inputId === DateType.toDate) {
        this._toDate = this._formatDateForDatepicker(parsedSelectedDate);
    }
    this.requestUpdate();
}


private _handleGoButton(){
    this._getLogsForVessel();
}
    render() {
        const isPremium:boolean = this._vessel?.vesselVersion === VesselVoyageVersion.Premium;

        return html`
          <section>
            <header>
              <button
                @click=${() => this._goToSubView(FiSubView.VesselAlarms)}
                data-test="back-button"
              >
                <mc-icon size="24" icon="chevron-left"></mc-icon>
              </button>
              <h1 class="premium-icon-h1">
                <span class="premium-icon" ?hidden=${!isPremium}>
                    <mc-icon color="#eac70d" icon="star-solid"></mc-icon>
                </span>${this._vessel?.vesselName || "N/A"} <br />${this.currentView === "vessel-alarms-log" ? "Alarm": "Notification"} log
              </h1>
            </header>
            <!-- Dropdown -->
            <div class="content-container">
              <div class="dates-wrapper">
                <div>
                  <div class="dropdown-container">
                    <div class="dropdown-title">
                      <label for="quick-date">Period</label>
                    </div>
                    <div class="dropdown-wrapper">
                      <voy-dropdown
                        .initialSelectedItem=${this._initialSelectedItem}
                        .customDisplayText=${this._selectedQuickDate}
                        .dropdownData=${dropdownData}
                        @item-selected=${(e: CustomEvent) =>
                          this._applyQuickDate(e.detail.selectedItem)}
                      ></voy-dropdown>
                    </div>
                  </div>
                  <div class="dates-wrapper">
                    <div class="dates-container">
                      <div class="input-and-header">
                        <div class="input-title">
                          <label for=${DateType.fromDate}>From</label>
                        </div>
                        <div class="input-container">
                          <input
                            id=${DateType.fromDate}
                            type="datetime-local"
                            min=${this._datepickerMinValue}
                            max=${this._datepickerMaxValue}
                            .value=${this._fromDate.substring(0, 16)}
                            @input=${this._dateHandler}
                            @change=${this._handleChangeDate}
                          />
                        </div>
                      </div>
                      <div class="input-and-header">
                        <div class="input-title">
                          <label for=${DateType.toDate}>To</label>
                        </div>
                        <div class="input-container">
                          <input
                            id=${DateType.toDate}
                            type="datetime-local"
                            min=${this._datepickerMinValue}
                            max=${this._datepickerMaxValue}
                            .value=${this._toDate.substring(0, 16)}
                            @input=${this._dateHandler}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <mc-button
                  class="go-button-styles"
                  label="Go"
                  fit="small"
                  @click=${this._handleGoButton}
                ></mc-button>
              </div>
            </div>
          </section>
          ${this._renderAlarms()}
        `;
    }

    private async _getLogsForVessel() {
        if (!this.imo) return;
        this._alarms = await VoyFleetAlarmModel?.getVesselAlarms(this.imo, [AlarmState.Pending, AlarmState.Cancelled], this._fromDate, this._toDate) || [];
        this._mergeNotificationAndAlarm();
    }

    connectedCallback() {
        super.connectedCallback();

        UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this._setVessel);
        VoyFleetAlarmModel!.addEventListener('alarms-updated', this._getLogsForVessel);
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        UiStateModel!.removeEventListener(`${UiStateModelParam.IMO}-change`, this._setVessel);
        VoyFleetAlarmModel!.removeEventListener('alarms-updated', this._getLogsForVessel);
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
    }

    firstUpdated() {
        // Bootstrapping
        this.currentView = UiStateModel?.getValue(UiStateModelParam.FISubView) ?? this.currentView;
        this._setVessel();
        this._getLogsForVessel();
    }

    update(changedProperties: PropertyValues) {
        if (changedProperties.has('imo')) {
            this._setVessel();
        }

        if (changedProperties.has('_alarms')){
            this._setLogsByDate()
        }

        super.update(changedProperties);
    }

    private _sortAlarmLogsByNewest(a:string, b:string) {
        return b.localeCompare(a)
    }

    private _goToSubView(view: FiSubView) {
        UiStateModel!.setValue(UiStateModelParam.FISubView, view);
    }

    private async _setVessel(): Promise<void> {
        if (!this.imo) return;
        this._vessel = VoyageShoreContextModel!.vessels.find(v => v.imoNo === this.imo)
        if(!this._vessel){
            await VesselsModel?.refreshVessels()
            this._vessel = VesselsModel!.getVessel(this.imo)
        }
    }

    private _setLogsByDate(): void {
        if (this._logList && this._logList.length > 0 ) {
            this._logsByDate = groupAlarmsByDate(this._logList)
        }
    }

    private _seeAlarmDetails(id:number,type: string):void {
            this._goToSubView(FiSubView.AlarmDetails)
            UiStateModel!.setValue(UiStateModelParam.VesselAlarmId, id.toString());
    }

    private _getUserName(a: FleetAlarm): string {
        if(a.alarmLogs && a.alarmLogs.length){
            const latestLog = a.alarmLogs[0]
            let userName = latestLog.userId
    
            if (userName?.toLowerCase() === 'system') {
                return userName
            }
        
            if(!latestLog.userData) {
                return "Unknown User"
            }
    
            return latestLog.userData?.displayName || "Unknown User";
        }
        return "system";
       
    }

    private _getTimeStamp(logs: FleetAlarmLog[], status:AlarmState){
        return logs.filter((log => log.toState === status))
    }

    private _renderAlarmLogs(alarms:FleetAlarm[], date: string): TemplateResult {
        if (!this.imo) return html``;

         // Javascript date object starts months at 0, subtract 1 from the month api provided
         const dateParts = date.split('-').map((s, i) => i === 1 ? parseInt(s) - 1 : parseInt(s))
         const dateObj = new Date(dateParts[0], dateParts[1], dateParts[2])

        return html`
            <div class="alarm-log-entry">
                <h2>${format(dateObj, 'dd LLLL yyyy')}</h2>
                ${alarms.sort((a,b) => this._sortAlarmLogsByNewest(a.startTs, b.startTs)).map(a => {
                    const viewAlarmDetails = a.alarmType !== AlarmType.HeavyWeather && a.alarmType !== AlarmType.ParametricRoll && a.alarmType !== AlarmType.WRPerformanceAlert && a.alarmType !== AlarmType.WRReject && a.alarmType !== AlarmType.WRSafetyAlert;
                    return html`
                    <div class="alarm" @click=${viewAlarmDetails ? () => this._seeAlarmDetails(a.id, a.alarmType) : null}>
                        <p class="alarm-info">
                            <span class="alarm-type ${a.alarmType !== AlarmType.HeavyWeather && a.alarmType !== AlarmType.ParametricRoll ? "capitalize" : ""}">${a.alarmType === AlarmType.HeavyWeather ? "Heavy weather" : a.alarmType === AlarmType.ParametricRoll ? "Parametric roll": (a.alarmType)}</span>
                            <span class="alarm-timestamp"> at ${GetUTCTimestamp(a.startTs)} UTC</span>
                        </p>
                        ${ this.currentView === 'vessel-alarms-log' ? html `
                            <div class="alarm-state">
                                ${getAlarmLogContent(a)}
                            </div>` : nothing
                        }
                    </div>`
                })}
            </div>
        `
    }

    private _renderAlarms():TemplateResult {
        let className = this._timeLineVisible ? "alarm-log-container" : "alarm-log-container alarm-log-container-full";
        if(this._logList === undefined) {
            return html`
            <div class="spinner-wrapper">
                <div class="loadingOverlay">
                    <voyage-spinner></voyage-spinner>
                </div>
            </div>` 
        }
        const isArrEmpty = Array.isArray(this._logList) && this._logList.length <= 0
        if( Object.keys(this._logsByDate).length <= 0 || isArrEmpty){
            return html` 
                <section id="no-data">
                    <h2>No log events</h2>
                    <p>${this._vessel?.vesselName} has never encoutered an alarm.</p>
                </section>
            `
        } 
        return html`
            <section class = ${className}>
                ${Object.keys(this._logsByDate).sort(this._sortAlarmLogsByNewest).map(key => this._renderAlarmLogs(this._logsByDate[key], key))}
            </section>
        `
    }

    private _renderAlarmReasonsAndNotes(alarmLog: FleetAlarmLog[]){
            if(alarmLog?.length) {
                    let reasonEntry = this._vessel?.vesselVersion === VesselVoyageVersion.Premium ? alarmLog.filter( log => log.reasonSource?.toLowerCase() === "vessel" ): alarmLog.filter( log => log.toState === AlarmState.Acknowledged );
                    if(reasonEntry.length){
                        return html`
                            <div class="alarm-comments-notes">
                            <p>Reason: ${reasonEntry[0].reason}</p> 
                            ${reasonEntry[0].note ? html`<p>Comment: ${reasonEntry[0].note}</p>`:  nothing }
                            ${this._vessel?.vesselVersion === VesselVoyageVersion.Premium ? html`<p class="user"> by vessel</p>` : nothing }
                            </div>
                        `
                    }               
            } 
            return nothing;
    }

    private _renderCreatedAt(log:any){
        return html`
            ${log.alarmLogs && log.alarmLogs.length ? html` at <span> ${GetFormattedDateNoYear(this._getTimeStamp(log.alarmLogs,log.alarmState)?.[0]?.createdAt)} UTC</span>`: nothing }

        `
    }

    private _toggleTimeLine():void {
        this._timeLineVisible = !this._timeLineVisible
    }

    private _mergeNotificationAndAlarm(){



        this._logList = [];
        if(this.currentView === "vessel-alarms-log"){
            const alarms = this._alarms.filter(alarm => ![AlarmType.WRPerformanceAlert, AlarmType.WRReject, AlarmType.WRSafetyAlert].includes(alarm.alarmType))
            this._logList = [...alarms]
        }
        if(this.currentView === "vessel-notifications-log"){
            const alarms = this._alarms.filter(alarm => [AlarmType.WRPerformanceAlert, AlarmType.WRReject, AlarmType.WRSafetyAlert].includes(alarm.alarmType))
            this._logList = [ ...alarms]
        }

        this._logList.sort((a, b) => {
            return b.startTs - a.startTs;
        });
            return this._logList;

    }

    private _renderNotificationReason(notificationResponse:NotificationResponses[]) {
        if(!(notificationResponse && notificationResponse.length)) return nothing;
        let parsedData = JSON.parse(notificationResponse[0].data)
        return html`
            <div class="alarm-comments-notes">
            ${parsedData.reason ? html`<p>Reason: ${parsedData.reason}</p>`:nothing }
                ${parsedData.comments ? html`<p>Comment: ${parsedData.comments}</p>`:  nothing }

            </div>
        `
    }
}
